import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../api';
import './StudentProfile.css';
import { resolveAssetUrl } from '../utils/assetUrl';
import AssignTaskModal from './AssignTaskModal';
import { useConfirm } from '../contexts/ConfirmContext';

const TYPE_LABELS = {
    desk: 'Стіл',
    game: 'Гра',
    quiz: 'Квіз',
    complex: 'Комплекс',
    'multi-desk': 'Комплексне сервірування',
    pizza: 'Конструктор піци',
    course: 'Курс'
};

// --- Sub-components for details ---

const MetaField = ({ label, value, span }) => (
    <div className="sp-meta-field" style={span ? { gridColumn: `span ${span}` } : {}}>
        <span className="sp-meta-label">{label}</span>
        <span className="sp-meta-value">{value}</span>
    </div>
);

const ScoreBlock = ({ score, sub, passed, label }) => (
    <div className={`sp-score-block ${passed ? 'pass' : 'fail'}`}>
        <div className="sp-score-big">{score}</div>
        {sub && <div className="sp-score-sub">{sub}</div>}
        <span className={`sp-pill ${passed ? 'pass' : 'fail'}`}>{label}</span>
    </div>
);

const ErrorSection = ({ label, children }) => (
    <div className="sp-section">
        {label && <div className="sp-section-label">{label}</div>}
        {children}
    </div>
);

const ErrorGroup = ({ label, children }) => (
    <div className="sp-error-group">
        <div className="sp-error-group-label">{label}</div>
        {children}
    </div>
);

const ErrorItem = ({ icon, name, hint, type }) => (
    <div className={`sp-error-item ${type}`}>
        <span>{icon || '🍽️'} {name}</span>
        <span className="sp-error-hint">{hint}</span>
    </div>
);

const VideoPlayerMini = ({ url }) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com|youtu\.be)\/(?:watch\?v=)?([^&]+)/);
    if (ytMatch) return <iframe title="yt" width="100%" height="300px"
        src={`https://www.youtube.com/embed/${ytMatch[1]}`} frameBorder="0" allowFullScreen />;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return <iframe title="vimeo"
        src={`https://player.vimeo.com/video/${vimeoMatch[1]}`} width="100%" height="300px" frameBorder="0" allowFullScreen />;
    const src = resolveAssetUrl(url);
    return <video controls style={{ width: '100%', maxHeight: '300px' }}><source src={src} /></video>;
};

const QuizAnswersList = ({ answers }) => (
    <ErrorSection label="🔍 Аналіз відповідей">
        {answers.map((a, i) => (
            <div key={i} className={`sp-answer ${a.isCorrect ? 'correct' : 'wrong'}`}>
                <div className="sp-answer-q">Питання {i + 1}: {a.questionText}</div>
                {a.image && <img src={resolveAssetUrl(a.image)} alt="" className="sp-answer-img" />}
                {a.video && <div className="sp-answer-video"><VideoPlayerMini url={a.video} /></div>}
                <div className="sp-answer-row">
                    <span className={`sp-answer-badge ${a.isCorrect ? 'correct' : 'wrong'}`}>
                        {a.isCorrect ? '✅' : '❌'} {a.givenAnswer}
                    </span>
                    {!a.isCorrect && (
                        <div className="sp-answer-correction">
                            <span className="sp-answer-correct">✓ {a.correctAnswer}</span>
                            {a.explanation && <div className="sp-answer-explain">{a.explanation}</div>}
                        </div>
                    )}
                </div>
            </div>
        ))}
    </ErrorSection>
);

const DetailModal = ({ show, onClose, children, title }) => {
    if (!show) return null;
    return (
        <div className="sp-modal-overlay" onClick={onClose}>
            <div className="sp-modal" onClick={e => e.stopPropagation()}>
                <div className="sp-modal-header">
                    <span className="sp-modal-title">{title}</span>
                    <button className="sp-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="sp-modal-body">{children}</div>
            </div>
        </div>
    );
};

const StudentProfile = ({ user }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [expandedCourses, setExpandedCourses] = useState({});

    const toggleCourseExpand = (courseId) => {
        setExpandedCourses(prev => ({
            ...prev,
            [courseId]: !prev[courseId]
        }));
    };
    
    // Edit Profile State
    const [historyPage, setHistoryPage] = useState(1);
    const [departmentsList, setDepartmentsList] = useState([]);
    const [citiesList, setCitiesList] = useState([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({
        studentName: '',
        studentLastName: '',
        studentCity: '',
        customCity: '',
        position: 'waiter',
        level: 'trainee',
        username: '',
        password: ''
    });

    const LEVEL_LABELS = {
        trainee: 'Стажер',
        junior: 'Молодший',
        regular: 'Основний',
        senior: 'Старший'
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const res = await axios.get(`${API_URL}/students/${id}`, config);
                setData(res.data);
                
                setEditFormData({
                    studentName: res.data.student.studentName || '',
                    studentLastName: res.data.student.studentLastName || '',
                    studentCity: res.data.student.studentCity || '',
                    customCity: '',
                    position: res.data.student.position || 'waiter',
                    level: res.data.student.level || 'trainee',
                    username: res.data.student.username || '',
                    password: ''
                });
            } catch (err) {
                console.error('Error fetching profile:', err);
                alert('Помилка завантаження профілю');
                navigate('/students');
            } finally {
                setLoading(false);
            }
        };

        const fetchCities = async () => {
            try {
                const res = await axios.get(`${API_URL}/cities`);
                setCitiesList(res.data);
            } catch (err) {
                console.error('Error fetching cities:', err);
            }
        };

        const fetchDepts = async () => {
            try {
                const res = await axios.get(`${API_URL}/departments`);
                setDepartmentsList(res.data);
            } catch (err) {
                console.error('Error fetching departments:', err);
            }
        };

        fetchProfile();
        fetchCities();
        fetchDepts();
    }, [id, navigate]);

    const confirm = useConfirm();

    const handleDeleteStudent = async () => {
        const isConfirmed = await confirm({
            title: 'Видалення акаунту',
            message: 'Ви впевнені, що хочете видалити цього гравця? Всі його облікові дані будуть назавжди видалені. Результати тестів будуть збережені для загальної статистики.',
            confirmText: 'Видалити',
            cancelText: 'Скасувати'
        });
        if (!isConfirmed) {
            return;
        }
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/students/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Гравця видалено успішно');
            navigate('/students');
        } catch (err) {
            console.error('Error deleting student:', err);
            alert('Не вдалося видалити гравця');
        }
    };

    const handleUpdateStudent = async (e) => {
        e.preventDefault();
        const city = editFormData.studentCity === 'custom' ? editFormData.customCity : editFormData.studentCity;
        if (!city || !city.trim()) {
            alert('Місто обов’язкове');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.put(`${API_URL}/students/${id}`, {
                ...editFormData,
                studentCity: city.trim()
            }, config);

            setIsEditModalOpen(false);
            
            // Reload page data
            const res = await axios.get(`${API_URL}/students/${id}`, config);
            setData(res.data);
            setEditFormData({
                studentName: res.data.student.studentName || '',
                studentLastName: res.data.student.studentLastName || '',
                studentCity: res.data.student.studentCity || '',
                customCity: '',
                position: res.data.student.position || 'waiter',
                level: res.data.student.level || 'trainee',
                username: res.data.student.username || '',
                password: ''
            });
        } catch (err) {
            console.error('Error updating student:', err);
            alert(err.response?.data?.error || 'Не вдалося оновити гравця');
        }
    };

    if (loading) return <div className="sp-loading">Вавантаження профілю...</div>;
    if (!data) return null;

    const handleToggleBlock = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.patch(`${API_URL}/students/${id}/block`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(prev => ({
                ...prev,
                student: { ...prev.student, isBlocked: res.data.isBlocked }
            }));
            // alert(res.data.isBlocked ? 'Акаунт заблоковано' : 'Акаунт розблоковано');
        } catch (err) {
            console.error('Error toggling block status:', err);
            alert('Не вдалося змінити статус блокування');
        }
    };

    const { student, history, recommendations, courses } = data;

    const completedCoursesHistory = (courses || [])
        .filter(item => item.progress?.isCompleted)
        .map(item => ({
            type: 'course',
            title: item.course.title,
            templateName: item.course.title,
            completedAt: item.progress.completedAt || item.progress.updatedAt || new Date(),
            percentage: item.progress.percentage || 100,
            score: item.progress.completedSteps,
            total: item.progress.totalSteps,
            passed: true
        }));

    const combinedHistory = [
        ...history,
        ...completedCoursesHistory
    ].sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

    const ITEMS_PER_PAGE = 8;
    const totalHistoryPages = Math.ceil(combinedHistory.length / ITEMS_PER_PAGE);
    const paginatedHistory = combinedHistory.slice((historyPage - 1) * ITEMS_PER_PAGE, historyPage * ITEMS_PER_PAGE);

    return (
        <div className="student-profile">
            <div className="sp-header">
                <button className="btn-back" onClick={() => navigate('/students')}>
                    ← Назад до списку
                </button>
                <div className="sp-header-actions">
                    <button 
                        className="btn-assign-task" 
                        onClick={() => setIsAssignModalOpen(true)}
                    >
                        🎯 Призначити завдання
                    </button>
                    {user?.role !== 'trainer' && (
                        <>
                            <button className="btn-edit-profile" onClick={() => setIsEditModalOpen(true)}>
                                ✏️ Редагувати
                            </button>
                            <button 
                                className={`btn-block-profile ${student.isBlocked ? 'unblock' : 'block'}`} 
                                onClick={handleToggleBlock}
                                style={{ backgroundColor: student.isBlocked ? '#4caf50' : '#f44336', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                {student.isBlocked ? '🔓 Розблокувати' : '🚫 Заблокувати'}
                            </button>
                            <button className="btn-delete-profile" onClick={handleDeleteStudent}>
                                🗑️ Видалити акаунт
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="sp-info-card">
                <div className="sp-main-info">
                    <div className="sp-large-avatar">{student.studentLastName[0]}{student.studentName[0]}</div>
                    <div className="sp-name-box">
                        <h2>
                            {student.studentLastName} {student.studentName}
                            {student.isBlocked && <span style={{ color: 'red', marginLeft: '10px', fontSize: '0.8em' }}>(Заблокований)</span>}
                        </h2>
                        <div className="sp-badges-row">
                            <span className="sp-city-badge">🏙️ {student.studentCity || 'Місто не вказано'}</span>
                            <span className={`sp-level-badge level-${student.level || 'trainee'}`}>
                                🏷️ {LEVEL_LABELS[student.level] || 'Стажер'}
                            </span>
                            <span className="sp-position-badge">
                                💼 {departmentsList.find(d => d.key === student.position)?.name || student.position || 'Офіціант'}
                            </span>
                            {student.username && <span className="sp-username-badge">👤 @{student.username}</span>}
                        </div>
                    </div>
                </div>
                <div className="sp-stats-grid">
                    <div className="sp-stat">
                        <span className="sp-stat-label">Середній бал</span>
                        <span className="sp-stat-value" style={{ color: student.avgScore >= 80 ? '#4ade80' : '#fbbf24' }}>
                            {student.avgScore}%
                        </span>
                    </div>
                    <div className="sp-stat">
                        <span className="sp-stat-label">Пройдено тестів</span>
                        <span className="sp-stat-value">{student.totalTests}</span>
                    </div>
                    <div className="sp-stat">
                        <span className="sp-stat-label">Остання активність</span>
                        <span className="sp-stat-date">{new Date(student.lastActivity).toLocaleDateString()}</span>
                    </div>
                </div>
            </div>

            {recommendations && (
                <div className={`sp-ai-card priority-${recommendations.priority || 'low'}`}>
                    <div className="sp-ai-header">
                        <div>
                            <h3>🤖 AI-рекомендації по гравцеві</h3>
                            <p>{recommendations.summary}</p>
                        </div>
                        <span className={`sp-ai-priority ${recommendations.priority || 'low'}`}>
                            {recommendations.priority === 'high' ? 'Високий пріоритет' : recommendations.priority === 'medium' ? 'Середній пріоритет' : 'Під контролем'}
                        </span>
                    </div>

                    {recommendations.appeal && (
                        <div className="sp-ai-appeal">
                            <span className="sp-ai-appeal-label">Звернення</span>
                            <p>{recommendations.appeal}</p>
                        </div>
                    )}

                    {recommendations.weakTypes?.length > 0 && (
                        <div className="sp-ai-weak-types">
                            {recommendations.weakTypes.map((item) => (
                                <span key={item.type} className={`sp-type-badge sp-type-${item.type}`}>
                                    {item.label}: {item.avg}%
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="sp-ai-actions">
                        {(recommendations.actions || []).map((action, index) => (
                            <div key={index} className={`sp-ai-action ${action.priority || 'low'}`}>
                                <div className="sp-ai-action-title">{action.title}</div>
                                <div className="sp-ai-action-detail">{action.detail}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="sp-courses-section">
                <h3>📚 Призначені курси та прогрес</h3>
                {courses && courses.length > 0 ? (
                    <div className="sp-courses-grid">
                        {courses.map((cData) => {
                            const { course, progress } = cData;
                            const isExpanded = expandedCourses[course._id];
                            return (
                                <div key={course._id} className="sp-course-card">
                                    <div className="sp-course-card-header" onClick={() => toggleCourseExpand(course._id)} style={{ cursor: 'pointer' }}>
                                        <div className="sp-course-info-row">
                                            {course.coverImage && (
                                                <img src={resolveAssetUrl(course.coverImage)} alt="" className="sp-course-cover-mini" />
                                            )}
                                            <div className="sp-course-text-details">
                                                <div className="sp-course-title">{course.title}</div>
                                                <div className="sp-course-meta">
                                                    {course.stageId?.title && <span className="sp-course-stage-badge">🏁 {course.stageId.title}</span>}
                                                    <span className="sp-course-steps-count">
                                                        📊 Кроків: {progress.completedSteps}/{progress.totalSteps}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="sp-course-status-side">
                                            <div className="sp-course-percentage">{progress.percentage}%</div>
                                            <span className={`sp-course-status-badge ${progress.isCompleted ? 'completed' : 'in-progress'}`}>
                                                {progress.isCompleted ? 'Виконано' : 'В процесі'}
                                            </span>
                                            <span className="sp-course-expand-arrow">{isExpanded ? '▲' : '▼'}</span>
                                        </div>
                                    </div>

                                    <div className="sp-course-progress-bar-container">
                                        <div className="sp-course-progress-bar-fill" style={{ width: `${progress.percentage}%` }}></div>
                                    </div>

                                    {isExpanded && (
                                        <div className="sp-course-steps-details">
                                            {course.modules?.map((module, mIdx) => (
                                                <div key={mIdx} className="sp-course-module-item">
                                                    <div className="sp-course-module-title">📦 Модуль {mIdx + 1}: {module.title}</div>
                                                    <div className="sp-course-steps-list">
                                                        {module.steps?.map((step) => {
                                                            const stepProgress = progress.stepsHistory?.find(sh => sh.stepId === step._id || sh.stepId?.toString() === step._id?.toString());
                                                            const status = stepProgress?.status || 'locked';
                                                            const score = stepProgress?.score || 0;
                                                            return (
                                                                <div key={step._id} className={`sp-course-step-item status-${status}`}>
                                                                    <div className="sp-course-step-left">
                                                                        <span className={`sp-step-status-bullet status-${status}`}></span>
                                                                        <span className="sp-step-title">{step.title}</span>
                                                                        <span className="sp-step-type-badge">{step.type}</span>
                                                                    </div>
                                                                    <div className="sp-course-step-right">
                                                                        {status === 'completed' && <span className="sp-step-completed-check">✅</span>}
                                                                        {status === 'pending' && <span className="sp-step-pending-label">Очікує</span>}
                                                                        {status === 'unlocked' && <span className="sp-step-unlocked-label">Доступно</span>}
                                                                        {status === 'locked' && <span className="sp-step-locked-label">🔒 Блок</span>}
                                                                        {score > 0 && <span className="sp-step-score">({score}%)</span>}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="sp-empty">Немає призначених курсів для міста цього гравця</div>
                )}
            </div>

            <div className="sp-history-section">
                <h3>📜 Історія проходжень ({combinedHistory.length})</h3>
                <div className="sp-history-scroll">
                    <div className="sp-timeline">
                        {paginatedHistory.map((item, idx) => (
                            <div key={idx} className="sp-history-item">
                                <div className="sp-item-date">
                                    <span className="sp-date-day">{new Date(item.completedAt).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' })}</span>
                                    <span className="sp-date-time">{new Date(item.completedAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                                <div className="sp-item-dot"></div>
                                <div className="sp-item-content clickable" onClick={() => item.type !== 'course' && setSelectedItem(item)}>
                                    <div className="sp-item-header">
                                        <span className={`sp-type-badge sp-type-${item.type}`}>
                                            {TYPE_LABELS[item.type] || item.type}
                                        </span>
                                        <span className="sp-template-name">
                                            {item.templateName || item.title || item.scenarioTitle || item.templateTitle || 'Назва невідома'}
                                        </span>
                                    </div>
                                    <div className="sp-item-result">
                                        <div className="sp-progress-mini">
                                            <div className="sp-progress-fill" style={{ 
                                                width: `${typeof item.percentage === 'number' ? item.percentage : (item.total > 0 ? (item.score / item.total * 100) : 0)}%`,
                                                backgroundColor: item.passed || item.isWin ? '#4ade80' : '#f87171'
                                            }}></div>
                                        </div>
                                        <span className="sp-score-text">
                                            {item.type === 'course' 
                                                ? `${item.score}/${item.total} уроків`
                                                : `${item.score}/${item.total} (${typeof item.percentage === 'number' ? item.percentage : (item.total > 0 ? Math.round(item.score / item.total * 100) : 0)}%)`
                                            }
                                        </span>
                                        <span className={`sp-status-icon ${item.passed || item.isWin ? 'success' : 'fail'}`}>
                                            {item.passed || item.isWin ? '✓' : '✗'}
                                        </span>
                                    </div>
                                    {item.type !== 'course' && (
                                        <div className="sp-item-footer">
                                            <span className="sp-view-detail">Переглянути деталі →</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {combinedHistory.length === 0 && <div className="sp-empty">Історія порожня</div>}
                    </div>
                </div>

                {totalHistoryPages > 1 && (
                    <div className="sp-pagination">
                        <button 
                            className="sp-pagination-btn"
                            disabled={historyPage === 1}
                            onClick={() => setHistoryPage(prev => Math.max(prev - 1, 1))}
                        >
                            ‹ Попередня
                        </button>
                        <span className="sp-pagination-info">
                            Сторінка {historyPage} з {totalHistoryPages} (всього {combinedHistory.length})
                        </span>
                        <button 
                            className="sp-pagination-btn"
                            disabled={historyPage === totalHistoryPages}
                            onClick={() => setHistoryPage(prev => Math.min(prev + 1, totalHistoryPages))}
                        >
                            Наступна ›
                        </button>
                    </div>
                )}
            </div>

            <DetailModal 
                show={!!selectedItem} 
                onClose={() => setSelectedItem(null)}
                title={selectedItem ? `${TYPE_LABELS[selectedItem.type]}: ${selectedItem.templateName || selectedItem.scenarioTitle || selectedItem.quizId?.title || selectedItem.complexTestId?.title || ''}` : ''}
            >
                {selectedItem && (
                    <div className="sp-detail-view">
                        <div className="sp-detail-meta">
                            <MetaField label="Дата" value={new Date(selectedItem.completedAt).toLocaleString()} />
                            <MetaField label="Результат" value={`${selectedItem.score} / ${selectedItem.total}`} />
                            <MetaField label="Відсоток" value={`${typeof selectedItem.percentage === 'number' ? selectedItem.percentage : (selectedItem.total > 0 ? Math.round(selectedItem.score / selectedItem.total * 100) : 0)}%`} />
                        </div>

                        {selectedItem.type === 'desk' && (
                            <div className="sp-desk-details">
                                <ScoreBlock 
                                    score={`${selectedItem.percentage}%`} 
                                    passed={selectedItem.passed} 
                                    label={selectedItem.passed ? 'Успішно' : 'Не здано'} 
                                />
                                {(selectedItem.userItems?.filter(i => !i.isCorrect).length > 0) && (
                                    <ErrorGroup label="Помилки">
                                        {selectedItem.userItems.filter(i => !i.isCorrect).map((it, i) => (
                                            <ErrorItem key={i} icon={it.icon} name={it.name || it.type} type="wrong" hint={`Неправильна позиція`} />
                                        ))}
                                    </ErrorGroup>
                                )}
                            </div>
                        )}

                        {selectedItem.type === 'game' && (
                            <div className="sp-game-details">
                                <ScoreBlock score={selectedItem.isWin ? '🎉' : '😔'} passed={selectedItem.isWin} label={selectedItem.isWin ? 'Перемога' : 'Поразка'} />
                                {selectedItem.choicePath?.length > 0 && (
                                    <div className="sp-path-list">
                                        {selectedItem.choicePath.map((cp, i) => (
                                            <div key={i} className="sp-path-step">
                                                <strong>{i + 1}.</strong> {cp.nodeText} → <em>{cp.choiceText}</em>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedItem.type === 'quiz' && <QuizAnswersList answers={selectedItem.answers || []} />}

                        {selectedItem.type === 'complex' && (
                            <div className="sp-complex-details">
                                {selectedItem.steps?.map((s, i) => (
                                    <div key={i} className={`sp-step-row ${s.passed ? 'pass' : 'fail'}`}>
                                        <span>{s.title}</span>
                                        <span>{s.score}/{s.total} ({s.percentage}%)</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </DetailModal>

            <DetailModal
                show={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title="✏️ Редагувати профіль гравця"
            >
                <form onSubmit={handleUpdateStudent} className="sp-edit-form">
                    <div className="sp-edit-grid">
                        <div className="form-group">
                            <label>Ім'я</label>
                            <input 
                                type="text" 
                                value={editFormData.studentName}
                                onChange={e => setEditFormData({...editFormData, studentName: e.target.value})}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Прізвище</label>
                            <input 
                                type="text" 
                                value={editFormData.studentLastName}
                                onChange={e => setEditFormData({...editFormData, studentLastName: e.target.value})}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Логін (username)</label>
                            <input 
                                type="text" 
                                value={editFormData.username}
                                onChange={e => setEditFormData({...editFormData, username: e.target.value})}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Новий пароль (порожній, якщо без змін)</label>
                            <input 
                                type="password" 
                                value={editFormData.password}
                                onChange={e => setEditFormData({...editFormData, password: e.target.value})}
                                placeholder="Новий пароль"
                            />
                        </div>
                        <div className="form-group">
                            <label>Місто</label>
                            <select 
                                value={editFormData.studentCity} 
                                onChange={e => setEditFormData({...editFormData, studentCity: e.target.value})}
                                required
                            >
                                <option value="">Оберіть місто...</option>
                                {citiesList.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                <option value="custom">+ Інше місто...</option>
                            </select>
                        </div>
                        {editFormData.studentCity === 'custom' && (
                            <div className="form-group">
                                <label>Введіть місто</label>
                                <input 
                                    type="text" 
                                    value={editFormData.customCity}
                                    onChange={e => setEditFormData({...editFormData, customCity: e.target.value})}
                                    required
                                />
                            </div>
                        )}
                        <div className="form-group">
                            <label>Посада / Підрозділ</label>
                            <select 
                                value={editFormData.position} 
                                onChange={e => setEditFormData({...editFormData, position: e.target.value})}
                                required
                            >
                                {departmentsList.length === 0 ? (
                                    <>
                                        <option value="waiter">🧑‍🍳 Офіціант</option>
                                        <option value="banquet_manager">🥂 Банкетний менеджер</option>
                                        <option value="administrator">💼 Адміністратор</option>
                                    </>
                                ) : (
                                    departmentsList.map(d => (
                                        <option key={d._id} value={d.key}>{d.name}</option>
                                    ))
                                )}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Рівень</label>
                            <select 
                                value={editFormData.level} 
                                onChange={e => setEditFormData({...editFormData, level: e.target.value})}
                                required
                            >
                                <option value="trainee">Стажер</option>
                                <option value="junior">Молодший</option>
                                <option value="regular">Основний</option>
                                <option value="senior">Старший</option>
                            </select>
                        </div>
                    </div>
                    <div className="sp-edit-actions">
                        <button type="submit" className="sp-btn-save-edit">Зберегти зміни</button>
                        <button type="button" className="sp-btn-cancel-edit" onClick={() => setIsEditModalOpen(false)}>Скасувати</button>
                    </div>
                </form>
            </DetailModal>
            {isAssignModalOpen && (
                <AssignTaskModal 
                    isOpen={isAssignModalOpen}
                    onClose={() => setIsAssignModalOpen(false)}
                    student={student}
                />
            )}
        </div>
    );
};

export default StudentProfile;
